import { useEffect, useState } from 'react'
import { useFormState } from 'react-final-form'

import Banner from 'common/components/banner'
import IconTitleGroup from 'common/components/iconTitleGroup'
import { AlertCircleInverted } from 'common/icons'

import LanguageOutputExamples from './languageOutputExamplesField/LanguageOutputExamplesField'
import IsoLanguageField from './IsoLanguageField'
import RuleInstructionField from './RuleInstructionField'

const MAX_RULES_PER_REGION = 10

type Props = {
  title: string
  description: string
  languageRulesCountByRegion?: Record<string, number>
  isEditMode?: boolean
}

const FormContent = ({
  title,
  description,
  isEditMode = false,
  languageRulesCountByRegion,
}: Props) => {
  const [isRuleLimitReached, setIsRuleLimitReached] = useState(false)
  const formState = useFormState()
  const { values } = formState

  const selectedRegion = values.region

  useEffect(() => {
    if (selectedRegion) {
      const regionId = selectedRegion.value
      const ruleCount = languageRulesCountByRegion?.[regionId] || 0
      setIsRuleLimitReached(ruleCount >= MAX_RULES_PER_REGION)
    }
  }, [selectedRegion, languageRulesCountByRegion])

  return (
    <>
      <div className="flex flex-col text-base-700 pb-5 border-b border-gold-400">
        <div className="text-2xl font-medium font-relais mb-4 px-6 pt-6">
          {title}
        </div>
        <div className="text-sm px-6">{description}</div>
      </div>
      <div className="flex flex-col p-6 overflow-auto h-full">
        <div>
          <IconTitleGroup title="Details" className="mb-4" />
          <IsoLanguageField
            isEditMode={isEditMode}
            isRuleLimitReached={isRuleLimitReached}
          />
          {selectedRegion && !isRuleLimitReached && <RuleInstructionField />}
        </div>
        {selectedRegion && !isRuleLimitReached && (
          <>
            <div className="flex flex-col text-base-700 my-6 border-b border-gold-400" />
            <LanguageOutputExamples />
          </>
        )}
        {selectedRegion && isRuleLimitReached && (
          <Banner
            className="mt-6"
            type="info"
            heading="Region not available"
            text={`We currently have a limit of ${MAX_RULES_PER_REGION} rules per region to maintain system performance. Rules should focus on solving critical grammatical issues, not minor adjustments.`}
            icon={
              <AlertCircleInverted
                className="text-base-700"
                isDefaultColor={false}
              />
            }
          />
        )}
      </div>
    </>
  )
}

export default FormContent
